import { v4 as uuid } from 'uuid';

import { isChildNode, isCommonNodeRoot, isSamePath } from '@utils/tree/commonTreeHelper';
import { createFlatTreePath } from '@utils/treeFormatter/treeFormatter';

import { ETreeTypeNode } from '@enums/tree';

import { ECompactXMLDataType, SELF_CLOSING_ELEMENT } from './constants';

import { IFlatXMLTreeNode, INonCompactXMLData, ITagStack, TMapTree } from './interface';
import { getLastChild } from './utils';

export function updateTagsForNonCommonNode({
	draftState,
	keys,
	startIndex,
	nodeToDelete,
	parentNode,
	value,
	lastDeletedElement,
}:{
	draftState: TMapTree;
	keys: string[];
	startIndex: number;
	nodeToDelete: IFlatXMLTreeNode | undefined;
	parentNode: IFlatXMLTreeNode | undefined;
	value: IFlatXMLTreeNode;
	lastDeletedElement: IFlatXMLTreeNode | undefined;
}) {
	const preNode = draftState.get(keys[startIndex - 1]);

	const lastChildAfterDeletion = getLastChild(
		draftState,
		nodeToDelete!.parentPath,
		keys.indexOf(parentNode!.id),
		keys,
	);

	const updatedTags = isCommonNodeRoot(value, parentNode!.path)
		? lastChildAfterDeletion?.tags || []
		: [
			...lastDeletedElement?.tags.filter(tag => {
				if (lastDeletedElement?.id !== undefined &&
					lastDeletedElement?.id === parentNode?.lastChild?.id) {

					return lastChildAfterDeletion?.id === parentNode.id
						? parentNode && tag.depth <= parentNode!.depth && lastChildAfterDeletion?.id === parentNode?.id
						: true;
				}

				return (
					tag.id !== parentNode!.id &&
					parentNode?.isEmptyObject &&
					tag.depth <= parentNode!.depth
				);

			},
			) || [],
		];

	const isUpdatedParentEmpty = !isCommonNodeRoot(value, parentNode!.path)
		? preNode && !isChildNode(preNode, parentNode!.path)
		: false;

	const updatedParentNode = {
		...parentNode!,
		isEmptyObject: isUpdatedParentEmpty,
		tags: updatedTags,
	};

	draftState.set(parentNode!.id, updatedParentNode);
}

export function updateNodeTagsAfterDeletion({
	draftState,
	parentNode,
	nodeToDelete,
	keys,
	lastDeletedElement,
	originalLastChild,
}: {
	draftState: TMapTree;
	parentNode?: IFlatXMLTreeNode;
	nodeToDelete?: IFlatXMLTreeNode;
	keys: string[];
	lastDeletedElement?: IFlatXMLTreeNode;
	originalLastChild?: IFlatXMLTreeNode;
},
) {
	let currentLineNumber = 1;
	const stack: ITagStack[] = [];

	const newKeys = Array.from(draftState).map(([key]) => key);
	const parentNodeData = draftState.get(parentNode!.id);
	const lastChildAfterDeletion = getLastChild(
		draftState,
		nodeToDelete!.parentPath,
		keys.indexOf(parentNode!.id),
		keys,
	);

	const isParentBecomeEmpty = lastChildAfterDeletion?.id === parentNodeData?.id;
	const isLastChildChanged = !isParentBecomeEmpty &&
		!!originalLastChild?.id &&
		originalLastChild?.id === lastDeletedElement?.id;

	const tags = isParentBecomeEmpty || isLastChildChanged
		? lastDeletedElement?.tags.filter(tag => {
			return tag.id !== lastDeletedElement?.id;
		})
		: lastChildAfterDeletion?.tags;

	const additionalNode: IFlatXMLTreeNode | undefined = lastChildAfterDeletion?.id && parentNodeData
		? {
			...parentNodeData,
			id: lastChildAfterDeletion?.id,
			key: lastChildAfterDeletion?.key,
			nodeType: lastChildAfterDeletion?.nodeType,
			tags: tags || [],
		}
		: undefined;

	const additionalTagsNode = !isLastChildChanged
		? lastChildAfterDeletion ? [lastChildAfterDeletion] : []
		: additionalNode ? [additionalNode] : [];

	for (let index = 0; index < newKeys.length; index++) {
		currentLineNumber = addClosingMapTreeTags(
			draftState,
			stack,
			newKeys[index],
			newKeys,
			currentLineNumber,
			index,
			additionalTagsNode,
		) || 0;
	}
}

const removeNodeFromStack = (stack: ITagStack[], additionalTagsNode?: IFlatXMLTreeNode[]) => {
	if (!additionalTagsNode?.length) return [];

	const tagIds = additionalTagsNode
		.flatMap(node => node.tags)
		.map(tag => tag.id);

	const indexesToRemove = stack
		.map((item, index) => tagIds.includes(item.id) ? index : -1)
		.filter(index => index !== -1);

	for (const index of [...indexesToRemove].sort((a, b) => b - a)) {
		stack.splice(index, 1);
	}

	return indexesToRemove;
};

export const updateLineNumbers = (
	map: [string, IFlatXMLTreeNode][],
	fromIndex: number,
) => {
	for (let i = fromIndex; i < map.length; i++) {
		map[i][1] = {
			...map[i][1],
			tags: map[i][1].tags.map((tag) => ({
				...tag,
				lineNumber: tag?.lineNumber ? tag.lineNumber + 1 : undefined,
			})),
		};
	}

	return map;
};

export const createFlatArrayFromXmlObject = ({
	data, flatData = new Map(), depth = 0, parentPath = '', keysParentPath, nonEditableKeys,
}: {
	data: INonCompactXMLData[];
	flatData?: TMapTree;
	depth?: number;
	parentPath?: string;
	keysParentPath?: string;
	nonEditableKeys?: Set<string>;
}): TMapTree => {
	for (const subNode of data) {
		const key = subNode.name;
		const uniqueKey = uuid();
		const path = createFlatTreePath(uniqueKey, parentPath);
		const keysPath = createFlatTreePath(subNode.name, keysParentPath);
		const isSelfClosingTag = subNode?.elements?.length === 0;
		const isObject = subNode?.elements?.[0]?.type === ECompactXMLDataType.element;
		const description = subNode?.attributes?.xmlns;

		const nodeType = isObject ? ETreeTypeNode.object : undefined;
		const initialValue = subNode?.elements?.[0]?.text;
		const value = isSelfClosingTag ? '' : initialValue;

		flatData.set(uniqueKey, {
			key,
			path,
			depth,
			keysPath,
			nodeType,
			defaultValue: value,
			parentPath: parentPath,
			lineNumber: 0,
			description,
			isCollapsed: false,
			isHiddenChildren: false,
			lastChild: null,
			tags: [],
			value,
			isChecked: false,
			isNonEditable: nonEditableKeys ? nonEditableKeys.has(key) : false,
			id: uniqueKey,
			isObjectAdded: false,
		});

		if (nodeType) {
			createFlatArrayFromXmlObject({
				data: subNode?.elements?.length === 0 ? SELF_CLOSING_ELEMENT : subNode?.elements,
				flatData: flatData,
				depth: depth + 1,
				parentPath: path,
				keysParentPath: keysPath,
				nonEditableKeys,
			});
		}
	}

	return flatData;
};

export const handleLastNode = (
	stack: ITagStack[],
	node: IFlatXMLTreeNode,
	currentLineNumber: number,
) => {
	node.tags = stack.reverse().map((item) => ({ ...item, lineNumber: (item.lineNumber || 0) + 1 }));
	node.lineNumber = currentLineNumber;
};

export const getCountOfTreeMapChildren = (
	map: TMapTree,
	parentKey: string,
	keys: string[],
	index: number,
) => {
	let count = 0;

	for (let i = index + 1; i < keys.length; i++) {
		const node = map.get(keys[i])!;

		if (isSamePath(node, parentKey)) {
			continue;
		} else if (node.path.length < parentKey.length || !isChildNode(node, parentKey)) {
			break;
		}

		count += 1 + (node.nodeType ? 1 : 0);
	}

	return count;
};

export const handleDepthDecreased = (
	node: IFlatXMLTreeNode,
	currentLineNumber: number,
	nodeTags: ITagStack[],
) => {
	if (!nodeTags.length) {
		return;
	}

	node.tags = nodeTags;

	if (!node.isCollapsed) {
		node.lineNumber = currentLineNumber;
	}
};

export const addLastMapTreeChild = (
	map: TMapTree,
	keys: string[],
) => {
	const lastChildIds = new Map<string, string>();

	for (let i = 0; i < keys.length; i++) {
		const node = map.get(keys[i]);

		if (!node || !node.nodeType) {
			continue;
		}

		const lastChild = getLastChild(map, node.path, i, keys);

		if (lastChild) {
			lastChildIds.set(node.id, lastChild.id);
		}
	}

	const newMap = new Map<string, IFlatXMLTreeNode>();

	for (const key of keys) {
		const node = map.get(key);

		if (node) {
			newMap.set(key, { ...node, lastChild: null });
		}
	}

	lastChildIds.forEach((lastChildId, nodeId) => {
		const node = newMap.get(nodeId);
		const lastChild = newMap.get(lastChildId);

		if (node && lastChild) {
			newMap.set(nodeId, { ...node, lastChild });
		}
	});

	newMap.forEach((value, key) => {
		map.set(key, value);
	});
};

export const getNodeXMLTags = (stack: ITagStack[], nextNode?: IFlatXMLTreeNode) => {
	const found = stack.find(({ depth }) => depth === nextNode?.depth);
	const lastEncounterOfDepth = found ? stack.lastIndexOf(found) : undefined;

	return lastEncounterOfDepth !== undefined ? stack.splice(lastEncounterOfDepth).reverse() : [];
};

export const getLastNotCollapsedMapTreeIndex = (
	map: TMapTree,
	id: string,
	keys: string[],
) => {
	let lastNotCollapsedIndex = keys.indexOf(id);

	while (map.get(keys[lastNotCollapsedIndex])?.isCollapsed && lastNotCollapsedIndex >= 0) {
		lastNotCollapsedIndex--;
	}

	return !map.get(keys[lastNotCollapsedIndex])?.isCollapsed ? keys[lastNotCollapsedIndex] : undefined;
};

export const handleEmptyObjectTags = ({
	map,
	node,
	id,
	currentLineNumber,
	additionalTagsNode,
}:{
	map: TMapTree;
	node: IFlatXMLTreeNode;
	id: string;
	currentLineNumber: number;
	additionalTagsNode?: IFlatXMLTreeNode[];
},
) => {
	const filteredAdditionalNodes = additionalTagsNode?.length && additionalTagsNode
		.filter(item => item.id === node.id)
		.map(item => ({ ...item, tags: item.tags.filter(tag => tag.id !== node.id) }));

	const tagsToAdd: ITagStack[] = filteredAdditionalNodes
			? filteredAdditionalNodes
				.map((item, index) => item.tags
					.map(item => ({ ...item, lineNumber: currentLineNumber + index + 1 })),
				).flat()
			: [];

	const newTags = [{
				depth: node.depth,
				value: node.key,
				id: node.id,
				lineNumber: currentLineNumber,
				isAdded: node.isObjectAdded,
			},
			...tagsToAdd,
		];

	const nodeWithTags: IFlatXMLTreeNode = {
		...node,
		lineNumber: currentLineNumber,
		tags: newTags,
	};

	map.set(id, nodeWithTags);

	return currentLineNumber + 2 + tagsToAdd.length;
};

const handleNodeDepthChanges = (
	node: IFlatXMLTreeNode,
	map: TMapTree,
	stack: ITagStack[],
	lastStackDepth: number | undefined,
	keys: string[],
	index: number,
	currentLineNumber: number,
): void => {
	const isNotHiddenNode = node.nodeType && !node.isHiddenChildren;
	const isDepthNoEqual = lastStackDepth !== node.depth;

	if (isNotHiddenNode && isDepthNoEqual) {
		const lineNumber = getCountOfTreeMapChildren(map, node.path, keys, index) + currentLineNumber;

		stack.push({
			depth: node.depth,
			value: node?.key,
			id: node.id,
			lineNumber: lineNumber,
			isAdded: node.isObjectAdded,
		});
	}
};

const processDepthDecreased = (
	updatedNode: IFlatXMLTreeNode,
	stack: ITagStack[],
	nextNode: IFlatXMLTreeNode,
	currentLineNumber: number,
): { updatedNode: IFlatXMLTreeNode; newLineNumber: number } => {
	const nodeTags = getNodeXMLTags(stack, nextNode);
	const tempNode = { ...updatedNode };

	handleDepthDecreased(
		tempNode,
		currentLineNumber,
		nodeTags,
	);

	const newUpdatedNode = {
		...updatedNode,
		tags: tempNode.tags,
		lineNumber: tempNode.lineNumber,
	};

	return {
		updatedNode: newUpdatedNode,
		newLineNumber: currentLineNumber + nodeTags.length,
	};
};

const processLastNode = (
	updatedNode: IFlatXMLTreeNode,
	id: string,
	lastKey: string,
	stack: ITagStack[],
	currentLineNumber: number,
): IFlatXMLTreeNode => {
	if (id !== lastKey || !stack.length) {
		return updatedNode;
	}

	const tempNode = { ...updatedNode };

	handleLastNode(stack, tempNode, currentLineNumber);

	return {
		...updatedNode,
		tags: tempNode.tags,
		lineNumber: tempNode.lineNumber,
	};
};

export const addClosingMapTreeTags = (
	map: TMapTree,
	stack: ITagStack[],
	id: string,
	keys: string[],
	currentLineNumber: number,
	index: number,
	additionalTagsNode?: IFlatXMLTreeNode[],
) => {
	const node = map.get(id)!;
	const nextNode = map.get(keys[index + 1])!;
	const lastStackDepth = stack[stack.length - 1]?.depth;
	const lastKey = keys[keys.length - 1];

	if (node.isEmptyObject) {
		removeNodeFromStack(stack, additionalTagsNode);

		return handleEmptyObjectTags({
			map,
			node,
			id,
			currentLineNumber,
			additionalTagsNode,
		});
	}

	let updatedNode = {
		...node,
		lineNumber: currentLineNumber,
	};

	handleNodeDepthChanges(node, map, stack, lastStackDepth, keys, index, currentLineNumber);

	const isNextDepthDecreased = lastStackDepth >= nextNode?.depth;

	if (isNextDepthDecreased) {
		const {
			updatedNode: processedNode,
			newLineNumber,
		} = processDepthDecreased(updatedNode, stack, nextNode, currentLineNumber);

		updatedNode = processedNode;
		currentLineNumber = newLineNumber;
	} else {
		updatedNode.tags = [];
	}

	updatedNode = processLastNode(updatedNode, id, lastKey, stack, currentLineNumber);

	map.set(id, updatedNode);

	return currentLineNumber + 1;
};

export const toggleEmptyObjectNode = (
	parentNode: IFlatXMLTreeNode,
	isParentCollapsing: boolean,
	lastChild: IFlatXMLTreeNode | null,
) => {
	if (isParentCollapsing) {
		parentNode.tags = parentNode.tags?.filter((tag) => tag.id !== parentNode.id || tag.depth < parentNode.depth);
		parentNode.isHiddenChildren = true;
	} else {
		parentNode.isHiddenChildren = false;
		parentNode.tags = [...(lastChild?.tags || []) ];
	}
};

export const updateParentNodeTags = ({
	lastTags, lastTagsIndex, isOpenedNodeEmpty, data, parentIndex,
}: {
	lastTags?: ITagStack[];
	lastTagsIndex?: number;
	isOpenedNodeEmpty?: boolean;
	data: IFlatXMLTreeNode[];
	parentIndex: number;
}) => {
	if (lastTagsIndex === undefined || lastTags === undefined || isOpenedNodeEmpty === undefined) {
		return;
	}

	const depthDiff = data[lastTagsIndex].depth - data[parentIndex].depth;
	const sliceStart = isOpenedNodeEmpty && data[lastTagsIndex].nodeType
		? depthDiff + 1
		: depthDiff;

	data[lastTagsIndex].tags = [];
	data[parentIndex].tags = lastTags.slice(sliceStart);
};

export const addClosingTagsXML = (
	data: TMapTree,
	stack: ITagStack[],
	startIndex: number = 0,
	keys: string[],
) => {
	let currentLineNumber = 1;

	for (let index = startIndex; index < keys.length; index++) {
		currentLineNumber = addClosingMapTreeTags(data, stack, keys[index], keys, currentLineNumber, index) || 0;
	}
};

